// src/lib/utils.js — extracted from App.js
import { supabase } from "./supabase";
import { SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY, CAPABILITIES, LOGO_URL, LOGO_WHITE_URL } from "../constants";

// ─── AI global flag ───────────────────────────────────────────────────────────
export let _aiEnabled = true; // loaded from app_settings on mount
export function setAiEnabled(val) { _aiEnabled = val; }

// ─── AI helpers ───────────────────────────────────────────────────────────────
export async function aiProxy(fn, payload) {
  const { data, error } = await supabase.functions.invoke("ai-proxy", { body: { fn, payload } });
  if (error) throw error;
  return data;
}


export async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = e => res(e.target.result.split(",")[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}


export async function aiSearch(query, inventory) {
  if (!_aiEnabled) {
    const q = query.toLowerCase();
    const matched = inventory.filter(w=>`${w.brand} ${w.model} ${w.decade}`.toLowerCase().includes(q)).map(w=>w.id);
    return { matchedIds:matched, summary:`Showing ${matched.length} result${matched.length!==1?"s":""} matching "${query}". (AI is currently disabled — results are keyword-matched only.)`, sourcing_note:"", confidence:"low" };
  }
  try {
    return await aiProxy("aiSearch", { query, inventory });
  } catch { return {matchedIds:[],summary:"Search complete.",sourcing_note:"",confidence:"low"}; }
}

// ─── Global CSS ───────────────────────────────────────────────────────────────

// ─── Time helper ─────────────────────────────────────────────────────────────
export function timeAgo(iso) {
  const s = (Date.now() - new Date(iso)) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
}


// ─── Email helpers ───────────────────────────────────────────────────────────
export function thcEmailHtml({ title, subtitle="", bodyHtml, footerNote="", codeBlock="" }) {
  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#F2EFE9;font-family:Georgia,'Times New Roman',serif">
<table width="100%" cellpadding="0" cellspacing="0" bgcolor="#F2EFE9" style="background:#F2EFE9;padding:40px 0 56px">
<tr><td align="center" style="padding:0 16px">
<table width="560" cellpadding="0" cellspacing="0" style="max-width:560px;width:100%">
  <tr><td bgcolor="#FFFFFF" style="background:#FFFFFF;padding:32px 48px 24px;text-align:center;border-bottom:2px solid #111118">
    <img src="${LOGO_URL}" alt="The Horological Catalog" width="260" style="width:260px;max-width:100%;display:inline-block"/>
  </td></tr>
  <tr><td bgcolor="#FFFFFF" style="background:#FFFFFF;padding:40px 48px">
    <div style="font-size:24px;color:#0C0C10;font-weight:400;margin-bottom:${subtitle?8:20}px;letter-spacing:0.01em">${title}</div>
    ${subtitle ? `<div style="font-family:-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif;font-size:13px;color:#52526A;line-height:1.7;margin-bottom:28px">${subtitle}</div>` : ""}
    ${codeBlock ? `<div style="background:#111118;color:#F0E8D8;font-family:'Courier New',Courier,monospace;font-size:32px;letter-spacing:0.35em;text-align:center;padding:22px;border-radius:4px;margin:0 0 28px">${codeBlock}</div>` : ""}
    <div style="font-family:-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif;font-size:13px;color:#3A3A46;line-height:1.75">${bodyHtml}</div>
    ${footerNote ? `<div style="margin-top:28px;padding-top:20px;border-top:1px solid #EDEBE6;font-family:-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif;font-size:11px;color:#8A8A9A;line-height:1.6">${footerNote}</div>` : ""}
  </td></tr>
  <tr><td bgcolor="#F2EFE9" style="background:#F2EFE9;padding:20px 48px;text-align:center">
    <a href="https://thc.watch" style="font-family:'Courier New',Courier,monospace;font-size:10px;color:#71717A;text-decoration:none;letter-spacing:0.12em">thc.watch</a>
    <div style="margin-top:8px;font-family:-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif;font-size:10px;color:#A1A1AA;line-height:1.7">The Horological Catalog &nbsp;&middot;&nbsp; &copy; 2024</div>
  </td></tr>
</table>
</td></tr></table>
</body></html>`;
}


export function notifEmailHtml(title, body, id=null) {
  return thcEmailHtml({
    title,
    bodyHtml: `<p style="margin:0 0 16px">${body}</p>`,
    codeBlock: id || "",
    footerNote: "You received this because you have an account on THC.watch. If you didn't expect this, you can ignore it.",
  });
}


export async function sendEmailNotif(to, subject, html) {
  try {
    await fetch(`${SUPABASE_FUNCTIONS_URL}/send-email`, {
      method:"POST",
      headers:{"Content-Type":"application/json","Authorization":`Bearer ${SUPABASE_ANON_KEY}`},
      body: JSON.stringify({to, subject, html})
    });
  } catch {}
}

// ─── Bell / Notification Dropdown ────────────────────────────────────────────

// ─── Capabilities email ───────────────────────────────────────────────────────
export function capabilitiesHtml(role, name) {
  const caps = CAPABILITIES[role] || CAPABILITIES.buyer;
  const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);
  const accent = role==="admin"?"#6D28D9":role==="seller"?"#B45309":role==="collector"?"#047857":"#1D4ED8";
  const firstStep = role==="seller"
    ? "Open your Seller Dashboard, complete your profile, and add your first listing. Turn on WhatsApp notifications so you never miss a sourcing request."
    : role==="collector"
    ? "Head to Add to Catalog, upload a watch photo and let our AI identify the reference for you — your first entry takes under two minutes."
    : role==="admin"
    ? "Review the Admin Panel for pending catalog submissions and collector applications awaiting your approval."
    : "Browse the Catalog, mark the watches you are hunting as ‘I want this’, then submit a Sourcing Request — our network of sellers will get to work immediately.";
  const capRows = caps.map((c, i) => [
    "<table width='100%' cellpadding='0' cellspacing='0' style='margin-bottom:" + (i===caps.length-1?0:18) + "px'>",
    "<tr>",
    "<td valign='top' style='width:38px'><div style='width:34px;height:34px;border-radius:50%;background:#F5F3EE;text-align:center;line-height:34px;font-size:15px'>" + c.icon + "</div></td>",
    "<td valign='top' style='padding-left:14px'>",
    "<div style='font-family:-apple-system,BlinkMacSystemFont,Helvetica Neue,Arial,sans-serif;font-size:13px;font-weight:600;color:#0C0C10;margin-bottom:3px'>" + c.title + "</div>",
    "<div style='font-family:-apple-system,BlinkMacSystemFont,Helvetica Neue,Arial,sans-serif;font-size:12px;color:#6B6B7A;line-height:1.65'>" + c.desc + "</div>",
    "</td></tr></table>"
  ].join("")).join("");
  return `<!DOCTYPE html>
<html lang="en" style="color-scheme:light">
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>Welcome — The Horological Catalog</title></head>
<body style="margin:0;padding:0;background:#F2EFE9;font-family:Georgia,'Times New Roman',serif">
<table width="100%" cellpadding="0" cellspacing="0" bgcolor="#F2EFE9" style="background:#F2EFE9;padding:52px 0 64px">
<tr><td align="center" style="padding:0 16px">
<table width="580" cellpadding="0" cellspacing="0" style="max-width:580px;width:100%">

  <!-- HEADER -->
  <tr><td bgcolor="#FFFFFF" style="background:#FFFFFF;padding:32px 52px 24px;text-align:center;border-bottom:2px solid #111118">
    <img src="${LOGO_URL}" alt="The Horological Catalog" width="280" style="width:280px;max-width:100%;display:inline-block"/>
  </td></tr>

  <!-- HERO -->
  <tr><td bgcolor="#FFFFFF" style="background:#FFFFFF;padding:36px 52px;text-align:center;border-bottom:3px solid ${accent}">
    <div style="display:inline-block;border:1px solid ${accent};color:${accent};font-family:'Courier New',Courier,monospace;font-size:9px;letter-spacing:0.22em;padding:5px 18px;text-transform:uppercase;margin-bottom:24px">${roleLabel} &nbsp;Membership</div>
    <div style="font-size:31px;color:#111118;line-height:1.35;font-weight:400;letter-spacing:0.01em">Your membership<br/>is confirmed.</div>
    <div style="margin-top:14px;font-family:-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif;font-size:13px;color:#71717A;line-height:1.8;max-width:340px;margin-left:auto;margin-right:auto">India's first AI-backed horological platform — the most inclusive community for vintage HMT, Allwyn and Ricoh timepieces.</div>
  </td></tr>

  <!-- BODY -->
  <tr><td bgcolor="#FFFFFF" style="background:#FFFFFF;padding:48px 52px">

    <div style="font-size:20px;font-weight:400;color:#0C0C10;margin-bottom:10px;letter-spacing:0.01em">Dear ${name},</div>
    <div style="font-family:-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif;font-size:13px;color:#58586A;line-height:1.8;margin-bottom:38px">You have joined as a <strong style="color:${accent};font-weight:600">${roleLabel}</strong>. Below is a full account of what the platform makes available to you — we hope you find it genuinely useful.</div>

    <div style="border-top:1px solid #EDEBE6;margin-bottom:32px"></div>

    ${capRows}

    <div style="border-top:1px solid #EDEBE6;margin:38px 0 32px"></div>

    <!-- First step -->
    <div style="background:#F7F5F0;border-left:2px solid ${accent};padding:20px 22px">
      <div style="font-family:'Courier New',Courier,monospace;font-size:9px;letter-spacing:0.22em;color:${accent};text-transform:uppercase;margin-bottom:10px">Where to begin</div>
      <div style="font-family:-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif;font-size:13px;color:#3A3A46;line-height:1.7">${firstStep}</div>
    </div>

    <!-- CTA -->
    <div style="text-align:center;margin-top:40px">
      <a href="https://thc.watch" style="display:inline-block;background:#0C0C10;color:#FFFFFF;text-decoration:none;font-family:-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif;font-size:11px;font-weight:700;padding:16px 48px;letter-spacing:0.1em;text-transform:uppercase">Enter the Catalog</a>
    </div>

    <!-- Sign-off -->
    <div style="margin-top:48px;border-top:1px solid #EDEBE6;padding-top:30px">
      <div style="font-size:14px;color:#0C0C10;margin-bottom:5px">With regards,</div>
      <div style="font-family:-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif;font-size:13px;color:#68687A">The THC Team</div>
      <div style="margin-top:18px;font-family:'Courier New',Courier,monospace;font-size:9px;letter-spacing:0.16em;color:#8A8A9A;text-transform:uppercase">The Horological Catalog &nbsp;&middot;&nbsp; thc.watch</div>
    </div>
  </td></tr>

  <!-- FOOTER -->
  <tr><td bgcolor="#F2EFE9" style="background:#F2EFE9;padding:24px 52px;text-align:center">
    <div style="font-family:'Courier New',Courier,monospace;font-size:9px;color:#A1A1AA;letter-spacing:0.2em;text-transform:uppercase;margin-bottom:10px">India's first AI-backed horological society</div>
    <a href="https://thc.watch" style="font-family:'Courier New',Courier,monospace;font-size:10px;color:#71717A;text-decoration:none;letter-spacing:0.12em">thc.watch</a>
    <div style="margin-top:10px;font-family:-apple-system,BlinkMacSystemFont,'Helvetica Neue',Arial,sans-serif;font-size:10px;color:#A1A1AA;line-height:1.7">You received this because you just created an account on THC.watch.<br/>&copy; 2024 The Horological Catalog. All rights reserved.</div>
  </td></tr>

</table>
</td></tr></table>
</body></html>`;
}


// ─── Image compression ───────────────────────────────────────────────────────
export function compressImage(file, maxPx=1200, quality=0.82) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      canvas.toBlob(blob => resolve(blob || file), "image/jpeg", quality);
    };
    img.onerror = () => resolve(file);
    img.src = url;
  });
}

// ─── AI government ID validation ─────────────────────────────────────────────

// ─── AI government ID validation ─────────────────────────────────────────────
export async function cropPhotoFromId(file) {
  if (!_aiEnabled) return null;
  try {
    const base64 = await fileToBase64(file);
    const bbox = await aiProxy("cropPhoto", { base64, mediaType: file.type || "image/jpeg" });
    if (!bbox.found) return null;
    return await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const iw=img.width, ih=img.height;
        const x=Math.round((bbox.left/100)*iw), y=Math.round((bbox.top/100)*ih);
        const w=Math.round((bbox.width/100)*iw), h=Math.round((bbox.height/100)*ih);
        const size=Math.max(w,h);
        const canvas=document.createElement("canvas");
        canvas.width=canvas.height=size;
        const ctx=canvas.getContext("2d");
        ctx.drawImage(img,x,y,w,h,0,0,size,size);
        canvas.toBlob(b=>resolve(b),"image/jpeg",0.92);
      };
      img.onerror=()=>resolve(null);
      img.src=URL.createObjectURL(file);
    });
  } catch { return null; }
}


export async function validateGovId(file) {
  if (!_aiEnabled) return { isValidId:true, reason:"AI validation disabled — accepted automatically." };
  try {
    const base64 = await fileToBase64(file);
    return await aiProxy("validateId", { base64, mediaType: file.type || "image/jpeg" });
  } catch { return null; }
}


export async function extractIdDetails(file) {
  if (!_aiEnabled) return null;
  try {
    const base64 = await fileToBase64(file);
    return await aiProxy("extractIdDetails", { base64, mediaType: file.type || "image/jpeg" });
  } catch { return null; }
}

// ─── AI photo detection ───────────────────────────────────────────────────────

// ─── AI photo detection ───────────────────────────────────────────────────────
export async function detectWatchFromPhoto(file) {
  if (!_aiEnabled) return null;
  try {
    const base64 = await fileToBase64(file);
    return await aiProxy("detectWatch", { base64, mediaType: file.type || "image/jpeg" });
  } catch { return null; }
}

// ─── Request Collector Access ─────────────────────────────────────────────────

// ─── Watch bucket grouping ────────────────────────────────────────────────────
export function groupCatalogEntries(entries) {
  const map = new Map();
  for (const e of entries) {
    const key = `${(e.brand||"").toLowerCase().trim()}|||${(e.model||"").toLowerCase().trim()}`;
    if (!map.has(key)) {
      map.set(key, { key, brand:e.brand, model:e.model, decade:"", condition:"", movement:"", description:"", photos:[], entries:[], wantedCount:0, forSaleCount:0, collectorCount:0, prices:[], latestAt:e.createdAt||"" });
    }
    const b = map.get(key);
    b.entries.push(e);
    if (e.photos?.length) b.photos.push(...e.photos);
    if      (e.entryType==="wanted")   b.wantedCount++;
    else if (e.entryType==="for_sale") b.forSaleCount++;
    else                               b.collectorCount++;
    if (e.price) b.prices.push(e.price);
    if (!b.description && e.description && e.entryType!=="wanted") b.description = e.description;
    if (!b.decade    && e.decade)   b.decade    = e.decade;
    if (!b.condition && e.condition && e.entryType!=="wanted") b.condition = e.condition;
    if (!b.movement  && e.movement) b.movement  = e.movement;
    if ((e.createdAt||"") > b.latestAt) b.latestAt = e.createdAt||"";
  }
  return [...map.values()].sort((a,b)=>b.latestAt.localeCompare(a.latestAt));
}

// Builds display cards using watch_models as canonical identity where available

export function buildCatalogDisplay(watchModels, catalogEntries) {
  const getUrl = p => typeof p==="string" ? p : p?.url || null;
  const approved = catalogEntries.filter(e=>e.status==="approved");
  const usedIds = new Set();
  // Primary: watch_model-linked entries (show even if no catalog_entries yet)
  const modelCards = watchModels.map(wm => {
    const entries = approved.filter(e=>e.watchModelId===wm.id);
    entries.forEach(e=>usedIds.add(e.id));
    const allPhotos = [...(wm.photos||[]).map(getUrl), ...entries.flatMap(e=>(e.photos||[]).map(getUrl))].filter(Boolean);
    const uniquePhotos = [...new Set(allPhotos)].slice(0,8);
    const primary = entries.find(e=>e.entryType!=="wanted") || entries[0];
    const prices = entries.filter(e=>e.price).map(e=>e.price);
    return { ...wm, id:wm.id, isWatchModel:true, entries, photos:uniquePhotos,
      wantedCount:entries.filter(e=>e.entryType==="wanted").length,
      forSaleCount:entries.filter(e=>e.entryType==="for_sale").length,
      collectorCount:entries.filter(e=>!e.entryType||e.entryType==="collector").length,
      prices, description:wm.description||primary?.description||"",
      decade:wm.decade||primary?.decade||"", condition:wm.condition||primary?.condition||"",
      movement:wm.movement||primary?.movement||"",
      latestAt:entries.reduce((m,e)=>(e.createdAt||"")>m?e.createdAt||"":m, wm.createdAt||""),
    };
  });
  // Orphan entries not yet linked to a model
  const orphans = groupCatalogEntries(approved.filter(e=>!usedIds.has(e.id)));
  return [...modelCards, ...orphans].sort((a,b)=>(b.latestAt||"").localeCompare(a.latestAt||""));
}

// ─── Admin inline catalog entry editor ───────────────────────────────────────

// ─── Offer helpers ───────────────────────────────────────────────────────────
export const isOfferLive = (o) => o && o.status === "open" && new Date(o.expiresAt) > new Date();
export const liveOffers = (r) => (r?.offers||[]).filter(isOfferLive);
export const acceptedOffers = (r) => (r?.offers||[]).filter(o => o.status === "accepted");
